import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import './App.css';

// Public site (loaded eagerly — these are needed on first paint)
import Nav from './components/Nav';
import Hero from './components/Hero';
import About from './components/About';
import Program from './components/Program';
import Artists from './components/Artists';
import FamilyActivities from './components/FamilyActivities';
import Partners from './components/Partners';
import BecomePartner from './components/BecomePartner';
import BrochureForm from './components/BrochureForm';
import CrazyVip from './components/CrazyVip';
import Mobility from './components/Mobility';
import Contact from './components/Contact';
const WinPage = lazy(() => import('./components/WinPage'));
import Footer from './components/Footer';
import EventJsonLd from './components/EventJsonLd';
import CookieConsent from './components/CookieConsent';
import Analytics from './components/Analytics';
import FloatingWinSticker from './components/FloatingWinSticker';

// Admin (code-split: only loaded when user navigates to /admin/*)
import { AuthProvider } from './lib/AuthContext';
const AdminLogin = lazy(() => import('./admin/AdminLogin'));
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./admin/AdminDashboard'));
const AdminFestivalDays = lazy(() => import('./admin/AdminFestivalDays'));
const AdminArtists = lazy(() => import('./admin/AdminArtists'));
const AdminPartners = lazy(() => import('./admin/AdminPartners'));
const AdminFamilyActivities = lazy(() => import('./admin/AdminFamilyActivities'));
const AdminParkings = lazy(() => import('./admin/AdminParkings'));
const AdminWinEntries = lazy(() => import('./admin/AdminWinEntries'));
const AdminVipRequests = lazy(() => import('./admin/AdminVipRequests'));
const AdminSettings = lazy(() => import('./admin/AdminSettings'));
const AdminInboxBrochure = lazy(() => import('./admin/AdminInboxBrochure'));
const AdminInboxContact = lazy(() => import('./admin/AdminInboxContact'));

// Legal pages (code-split — rarely visited, no need to bloat main bundle)
const LegalPage = lazy(() => import('./pages/LegalPage'));

const AdminFallback = () => (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 text-sm">
        Even laden…
    </div>
);

/**
 * Smart scroll manager — runs on every router-driven location change.
 *  - hash empty: scroll to top of page (instant; user expects a fresh document)
 *  - hash present: scroll to the matching element. Uses rAF + a 200 ms retry
 *    so cross-route nav (e.g. /privacy → /#programma) works even when the
 *    target section is not yet mounted.
 */
const ScrollManager = () => {
    const location = useLocation();
    const { pathname, hash, key } = location;
    useEffect(() => {
        if (hash) {
            const id = decodeURIComponent(hash.slice(1).split('?')[0]);
            const tryScroll = () => {
                const el = document.getElementById(id);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    return true;
                }
                return false;
            };
            // rAF + 250 ms retry covers both same-page hash nav and cross-route nav
            // where the target section is not yet mounted.
            requestAnimationFrame(() => {
                if (!tryScroll()) setTimeout(tryScroll, 250);
            });
        } else {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
        // CRITICAL: include `key` so the effect re-fires on every navigation,
        // including clicks on the SAME anchor (where pathname+hash are unchanged).
        // React-router assigns a fresh `key` to every Location push.
    }, [pathname, hash, key]);
    return null;
};

const PublicSite = () => (
    <div className="App min-h-screen bg-cream font-body" data-testid="app-root">
        <EventJsonLd />
        <Nav />
        <main>
            <Hero />
            <About />
            <Program />
            <Artists />
            <FamilyActivities />
            <Partners />
            <BecomePartner />
            <section data-testid="brochure-section" className="relative py-16 sm:py-20 bg-cream">
                <div className="mx-auto max-w-3xl px-5 sm:px-8">
                    <BrochureForm />
                </div>
            </section>
            <CrazyVip />
            <Mobility />
            <Contact />
        </main>
        <Footer />
    </div>
);

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <Toaster position="top-right" richColors closeButton />
                <Analytics />
                <ScrollManager />
                <Suspense fallback={<AdminFallback />}>
                    <Routes>
                        <Route path="/" element={<PublicSite />} />
                        <Route path="/win" element={<WinPage />} />
                        <Route path="/privacy" element={<LegalPage kind="privacy" />} />
                        <Route path="/cookies" element={<LegalPage kind="cookies" />} />
                        <Route path="/algemene-voorwaarden" element={<LegalPage kind="algemene-voorwaarden" />} />
                        <Route path="/wettelijke-vermeldingen" element={<LegalPage kind="wettelijke-vermeldingen" />} />
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin" element={<AdminLayout />}>
                            <Route index element={<AdminDashboard />} />
                            <Route path="festival-days" element={<AdminFestivalDays />} />
                            <Route path="artists" element={<AdminArtists />} />
                            <Route path="partners" element={<AdminPartners />} />
                            <Route path="family-activities" element={<AdminFamilyActivities />} />
                            <Route path="parkings" element={<AdminParkings />} />
                            <Route path="win-entries" element={<AdminWinEntries />} />
                            <Route path="vip-requests" element={<AdminVipRequests />} />
                            <Route path="settings" element={<AdminSettings />} />
                            <Route path="inbox/brochure-requests" element={<AdminInboxBrochure />} />
                            <Route path="inbox/contact-messages" element={<AdminInboxContact />} />
                        </Route>
                    </Routes>
                </Suspense>
                <FloatingWinSticker />
                <CookieConsent />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
